/**
 * 章节引用模态框组件
 *
 * 提供工作区章节引用功能，支持：
 * - 从后端获取章节列表
 * - 选择章节并插入 #{chapter_title} 引用标记
 * - 显示加载状态
 * - 键盘快捷键 Ctrl+Shift+#
 * - 淡黄色主题配色
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChapterService } from '../../services/chapter';

/**
 * 章节信息接口
 */
interface ChapterInfo {
  id: number;
  name: string;
  word_count: number;
  num: number;
}

/**
 * ChapterReferenceModal组件属性接口
 */
interface ChapterReferenceModalProps {
  /** 是否显示模态框 */
  isOpen: boolean;
  /** 关闭模态框回调 */
  onClose: () => void;
  /** 选择章节后的回调，返回引用标记 */
  onSelectChapter: (reference: string) => void;
  /** JWT认证令牌 */
  token: string;
  /** 工作区ID */
  workspaceId: number;
}

/**
 * 关闭图标
 */
const XIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/**
 * 搜索图标
 */
const SearchIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

/**
 * 加载图标
 */
const SpinnerIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

/**
 * 文件图标
 */
const FileIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

/**
 * ChapterReferenceModal组件
 */
export const ChapterReferenceModal: React.FC<ChapterReferenceModalProps> = React.memo(({
  isOpen,
  onClose,
  onSelectChapter,
  token,
  workspaceId,
}) => {
  // ===== 状态管理 =====
  const [chapters, setChapters] = useState<ChapterInfo[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredChapters, setFilteredChapters] = useState<ChapterInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * 获取章节列表
   */
  useEffect(() => {
    const fetchChapters = async () => {
      if (!isOpen || !token || !workspaceId) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const chapterService = new ChapterService(undefined, token, workspaceId);
        const chaptersData = await chapterService.listChapters(workspaceId);

        if (Array.isArray(chaptersData)) {
          setChapters(chaptersData);
          setFilteredChapters(chaptersData);
        } else {
          setChapters([]);
          setFilteredChapters([]);
        }
      } catch (err) {
        console.error('Failed to fetch chapters:', err);
        setError('获取章节列表失败');
        setChapters([]);
        setFilteredChapters([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChapters();
  }, [isOpen, token, workspaceId]);

  /**
   * 搜索过滤章节
   */
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredChapters(chapters);
      setFocusedIndex(null);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = chapters.filter((chapter) =>
      chapter.name.toLowerCase().includes(query) ||
      chapter.num.toString().includes(query)
    );
    setFilteredChapters(filtered);
    setFocusedIndex(null);
  }, [searchQuery, chapters]);

  /**
   * 模态框打开时聚焦搜索框
   */
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * 键盘快捷键 Ctrl+Shift+#
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === '#') {
        e.preventDefault();
        if (!isOpen) {
          // 需要父组件打开模态框
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  /**
   * 选择章节
   */
  const handleSelectChapter = useCallback((chapter: ChapterInfo) => {
    const reference = `#{${chapter.name}}`;
    onSelectChapter(reference);
    handleClose();
  }, [onSelectChapter]);

  /**
   * 关闭模态框
   */
  const handleClose = useCallback(() => {
    setSearchQuery('');
    setFilteredChapters(chapters);
    setError(null);
    onClose();
  }, [onClose, chapters]);

  /**
   * 处理搜索输入变化
   */
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  /**
   * 处理键盘导航
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();

      if (filteredChapters.length === 0) return;

      setFocusedIndex((prev) => {
        if (prev === null) {
          return e.key === 'ArrowDown' ? 0 : filteredChapters.length - 1;
        }

        if (e.key === 'ArrowDown') {
          return (prev + 1) % filteredChapters.length;
        } else {
          return (prev - 1 + filteredChapters.length) % filteredChapters.length;
        }
      });
      return;
    }

    if (e.key === 'Enter' && focusedIndex !== null && focusedIndex >= 0 && focusedIndex < filteredChapters.length) {
      e.preventDefault();
      handleSelectChapter(filteredChapters[focusedIndex]);
    }
  }, [filteredChapters, focusedIndex, handleSelectChapter, handleClose]);

  /**
   * 点击背景关闭
   */
  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  /**
   * 确保聚焦的选项可见
   */
  useEffect(() => {
    if (focusedIndex !== null && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  // ===== 渲染函数 =====

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chapters-modal-title"
    >
      <div className="relative bg-white rounded-lg shadow-sm max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col border-2 border-gray-200">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg">
          <h2 id="chapters-modal-title" className="text-lg font-semibold text-gray-900">
            引用章节
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 rounded"
            aria-label="关闭"
          >
            <XIcon />
          </button>
        </div>

        {/* 搜索框 */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="搜索章节名称或序号..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent text-sm"
              aria-label="搜索章节"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <SearchIcon className="w-5 h-5" />
            </div>
          </div>
          {/* 快捷键提示 */}
          <div className="mt-2 text-xs text-gray-600">
            提示：使用键盘快捷键 Ctrl+Shift+# 快速打开
          </div>
        </div>

        {/* 章节列表 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <SpinnerIcon className="w-6 h-6 text-gray-700" />
              <span className="ml-2 text-sm text-gray-700">加载中...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 mb-4">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                }}
                className="px-4 py-2 text-sm bg-ink-800 hover:bg-ink-700 text-white rounded-lg shadow-sm hover:shadow-sm active:scale-[0.98] transition-all duration-150"
              >
                重试
              </button>
            </div>
          ) : filteredChapters.length === 0 ? (
            <div className="text-center py-8">
              {searchQuery ? (
                <p className="text-sm text-gray-600">未找到匹配的章节</p>
              ) : (
                <p className="text-sm text-gray-600">暂无章节</p>
              )}
            </div>
          ) : (
            <div ref={listRef} className="space-y-2" role="listbox" aria-label="章节列表">
              {filteredChapters.map((chapter, index) => {
                const isFocused = index === focusedIndex;

                return (
                  <button
                    key={chapter.id}
                    onClick={() => handleSelectChapter(chapter)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    onMouseLeave={() => setFocusedIndex(null)}
                    className={`
                      w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center
                      ${isFocused
                        ? 'bg-gray-50 outline-none ring-2 ring-gray-500'
                        : 'hover:bg-white'
                      }
                    `}
                    role="option"
                    aria-selected={false}
                    tabIndex={-1}
                  >
                    <FileIcon className="flex-shrink-0 w-4 h-4 mr-3 text-gray-600" />
                    <span className="flex-1 text-sm text-gray-900">{chapter.num}. {chapter.name}</span>
                    <span className="text-xs text-gray-600 ml-2">
                      {chapter.word_count.toLocaleString()} 字
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部 */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              找到 {filteredChapters.length} 个章节
            </div>
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

ChapterReferenceModal.displayName = 'ChapterReferenceModal';

export default ChapterReferenceModal;
